<template>
  <BaseModal
    v-if="products.length > 0"
    :is-open="isOpen"
    size="md"
    @close="close(actionClose.close)"
  >
    <template #header>
      <NavBar>
        <span slot="center">Termos de Produto</span>
        <span slot="left">
          <BackButton
            data-testid="back-button"
            @click="close(actionClose.back)"
          />
        </span>
        <span slot="right">
          <CloseButton
            data-testid="close-button"
            @click="close(actionClose.close)"
          />
        </span>
      </NavBar>
    </template>
    <template #content>
      <div class="selected-product-content">
        <div class="advise-text">
          <BaseText :color="NebraskaColors.textPrimary">
            Alguns produtos da sugestão precisam que você aceite os termos de
            adesão e ciência de risco. Verifique as principais carecterísticas e
            regras do produto antes de começar a investir
          </BaseText>
        </div>
        <p class="product-title">Produtos</p>

        <div v-for="product in products" :key="product.apiId">
          <ProductDetail>
            <template #title>
              <BaseText>{{ product.name }}</BaseText>
            </template>
            <template #content>
              <div
                v-for="documents in product.documents"
                :key="documents.url"
                class="documents"
              >
                <Row
                  :data-testid="'terms-acceptance-' + documents.title"
                  :has-line="false"
                  :has-interaction="true"
                  @click="goToDocument(documents.url)"
                >
                  <template #top-left>
                    <b>{{ documents.title }}</b>
                  </template>
                  <template #icon-right>
                    <BaseIcon
                      class="margin-icon"
                      :colors="NebraskaColors.baseSecondary"
                      icon="ic_arrowUp"
                      height="12"
                      width="12"
                    />
                  </template>
                </Row>
              </div>
            </template>
          </ProductDetail>
        </div>
      </div>
    </template>
    <template #footer>
      <div
        class="wrn-checkbox-container wrn-c-12 wrn-color-black wrn-input-lg checkbox-section"
      >
        <Checkbox
          v-model="checkboxValue"
          class="wrn-bg-white wrn-checkbox"
          :class="{ 'wrn-checked': checkboxValue }"
          @input="handleClickCheckbox"
        >
          <BaseText :color="NebraskaColors.textSecundary">
            Li e concordo em investir em todos produtos listados acima.
          </BaseText>
        </Checkbox>
      </div>
      <ModalFooter
        slot="footer"
        left-button-label="Cancelar"
        right-button-label="Continuar"
        :disabled-right-button="!checkboxValue"
        @left-button-clicked="close(actionClose.cancelled)"
        @right-button-clicked="emitAccepted"
      />
    </template>
  </BaseModal>
</template>

<script lang="ts">
import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import { ModalFooter } from '@/modules/common/components/__deprecated__/Modal';
import {
  NavBar,
  BaseText,
  Row,
  BaseIcon,
  BaseModal
} from '@warrenbrasil/nebraska-web';
import { CloseButton } from '@/modules/common/components/buttons';
import { BackButton } from '@/modules/common/components/buttons/BackButton';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { Checkbox } from '@/modules/common/components/__deprecated__/Checkbox';
import { ProductDetail } from '@/modules/common/components/ProductDetail';
import * as termsTracker from './track-events';
import { InstrumentDetail } from '@/modules/trade/types/instrument';

enum actionClose {
  back = 'back',
  close = 'close',
  cancelled = 'cancelled'
}

@Component({
  components: {
    BaseModal,
    ModalFooter,
    CloseButton,
    NavBar,
    BaseText,
    Checkbox,
    Row,
    BaseIcon,
    ProductDetail,
    BackButton
  }
})
export default class TermsAcceptanceModal extends Vue {
  @Prop({ type: Boolean, required: false, default: false })
  public isOpen!: boolean;

  @Prop({ type: Array, required: true })
  public products!: InstrumentDetail[];

  @Prop({ type: Boolean, required: false })
  public viewTerms!: boolean;

  @Prop({ type: Boolean, default: false })
  public isInvestmentByProduct!: boolean;

  @Prop({ type: Boolean, default: false })
  public suggestInvestment!: boolean;

  private NebraskaColors = NebraskaColors;
  private checkboxValue = false;

  public get actionClose() {
    return actionClose;
  }

  private goToDocument(path: string) {
    window.open(path, '_blank', 'resizable=yes');
  }

  public handleClickCheckbox() {
    if (!this.isInvestmentByProduct && !this.suggestInvestment) {
      termsTracker.trackCheckbox();
    } else if (this.isInvestmentByProduct) {
      termsTracker.trackInvestmentCheckBox();
    } else if (this.suggestInvestment) {
      termsTracker.trackAcceptedRisks();
    }
  }

  @Emit('cancelled')
  public close(action: string) {
    if (!this.isInvestmentByProduct && !this.suggestInvestment) {
      termsTracker.trackCancelled();
    } else if (this.isInvestmentByProduct) {
      if (action === actionClose.back) termsTracker.trackInvestmentBack();
      if (action === actionClose.close) termsTracker.trackInvestmentClose();
      if (action === actionClose.cancelled)
        termsTracker.trackInvestmentCancelled();
    } else if (this.suggestInvestment) {
      if (action === actionClose.back) termsTracker.trackBackBtn();
      if (action === actionClose.close) termsTracker.trackCloseBtn();
      if (action === actionClose.cancelled) termsTracker.trackCancelTerms();
    }
  }

  @Emit('accepted')
  public emitAccepted() {
    if (!this.isInvestmentByProduct && !this.suggestInvestment) {
      termsTracker.trackAccepted();
    } else if (this.isInvestmentByProduct) {
      termsTracker.trackInvestmentAccepted();
    } else if (this.suggestInvestment) {
      termsTracker.trackContinueTerms();
    }
  }

  private beforeMount() {
    if (!this.products.length) {
      this.$emit('update:viewTerms', false);
      this.$emit('accepted');
    }
  }
}
</script>

<style lang="less" scoped>
.selected-product-content {
  padding: calc(@size-spacing-x600 + @size-spacing-x300) @size-spacing-x800 0
    @size-spacing-x800;
}

.advise-text {
  text-align: justify;
}

.product-title {
  margin: @size-spacing-x600 0 @size-spacing-x350 0;
  text-align: justify;
  color: @text-secondary;
  font-size: 14px;
  line-height: 18px;
  font-weight: bold;
  text-transform: uppercase;
}

.documents {
  width: 100%;
}

.margin-icon {
  margin-right: 8px;
}

.checkbox-section {
  border-top: 1px solid @divider-primary;
  padding: @size-spacing-x300 @size-spacing-x800;
}

/deep/ .scroll-shadow__content {
  padding: 0 0 8px 0;
}
</style>
